import { markSourceChangesRefreshPerformed } from "./sourceChangeExactSelection.js";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function readLoadingConstraintRecovery(payload) {
  if (payload?.outcome !== "confirmation_required") return null;
  const fields = ["dispatch_date", "dispatch_period", "preview_revision", "truck_id", "change_revision", "recovery_revision"];
  if (fields.some((key) => !normalizeInlineText(payload[key]))) return null;
  if (!Array.isArray(payload.proposed_groups) || payload.proposed_groups.length === 0) return null;
  const seenOrders = new Set();
  const seenGroups = new Set();
  for (const group of payload.proposed_groups) {
    const key = normalizeInlineText(group?.source_group_key);
    if (!key || seenGroups.has(key) || !Array.isArray(group?.order_ids) || !group.order_ids.length) return null;
    seenGroups.add(key);
    for (const value of group.order_ids) {
      const orderId = normalizeInlineText(value);
      if (!orderId || seenOrders.has(orderId)) return null;
      seenOrders.add(orderId);
    }
  }
  return {
    dispatchDate: payload.dispatch_date,
    dispatchPeriod: payload.dispatch_period,
    previewRevision: payload.preview_revision,
    truckId: payload.truck_id,
    changeRevision: payload.change_revision,
    recoveryRevision: payload.recovery_revision,
    proposedGroups: payload.proposed_groups.map((group) => ({ ...group, order_ids: [...group.order_ids] })),
  };
}

export function buildLoadingConstraintRecoveryDialog(recovery, truckLabel) {
  const groups = recovery.proposedGroups;
  const orderCount = groups.reduce((sum, group) => sum + group.order_ids.length, 0);
  const names = [...new Set(groups.map((group) => normalizeInlineText(group.label)).filter(Boolean))];
  const commandLabel = orderCount === 1 ? "la commande" : `les ${orderCount} commandes`;
  return {
    title: `Déplacer ${commandLabel}${names.length === 1 ? ` de ${names[0]}` : ""} vers Suspendues ?`,
    description: `Camion ${truckLabel || recovery.truckId} : le recalcul ne respecte pas les nouveaux horaires.`,
    impactItems: groups.flatMap((group) => [
      group.message || `${group.label} : chargement incompatible avec les nouveaux horaires.`,
      `Commandes : ${group.order_ids.join(", ")}.`,
    ]),
  };
}

function normalizeSelection(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const previewRevision = normalizeInlineText(value.preview_revision || value.previewRevision);
  const truckId = normalizeInlineText(value.truck_id || value.truckId);
  const changeRevision = normalizeInlineText(value.change_revision || value.changeRevision);
  return previewRevision && truckId && changeRevision
    ? { previewRevision, truckId, changeRevision }
    : null;
}

function isStalePreviewError(error, staleCodes) {
  const normalizedCodes = new Set(
    (Array.isArray(staleCodes) ? staleCodes : [staleCodes])
      .map(normalizeInlineText)
      .filter(Boolean),
  );
  return Number(error?.status) === 409
    && normalizedCodes.has(normalizeInlineText(error?.code));
}

/**
 * Retry one mill-constraint mutation after a fresh preview only when the exact
 * truck change is still present. A real concurrent change therefore remains
 * fail-closed while a stale read cache is repaired transparently.
 */
export async function executeLoadingConstraintSourceChangeRequest({
  initialRequestBody,
  postRequest,
  refreshSelection,
  staleCodes = ["preview_stale"],
} = {}) {
  if (typeof postRequest !== "function") {
    throw new TypeError("La fonction de mutation meunerie est requise.");
  }

  try {
    return await postRequest(initialRequestBody);
  } catch (error) {
    if (!isStalePreviewError(error, staleCodes) || typeof refreshSelection !== "function") {
      throw error;
    }

    let refreshedSelection;
    try {
      refreshedSelection = normalizeSelection(await refreshSelection());
    } catch (refreshError) {
      markSourceChangesRefreshPerformed(refreshError);
      throw refreshError;
    }
    const initialSelection = normalizeSelection(initialRequestBody);
    const selectionStillMatches = Boolean(
      initialSelection
      && refreshedSelection
      && refreshedSelection.truckId === initialSelection.truckId
      && refreshedSelection.changeRevision === initialSelection.changeRevision,
    );
    if (!selectionStillMatches) {
      markSourceChangesRefreshPerformed(error);
      throw error;
    }

    try {
      return await postRequest({
        ...initialRequestBody,
        preview_revision: refreshedSelection.previewRevision,
      });
    } catch (retryError) {
      markSourceChangesRefreshPerformed(retryError);
      throw retryError;
    }
  }
}
